import React from 'react';

import { SupportedServices } from '@ariksa/data-scanning/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { map } from 'lodash';
import { colorOpacity, customTheme } from 'theme';

import {
  EllipsisText,
  formatTooltip,
  PlusCountTag,
  Tag,
} from 'components/DataDisplay';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { BitbucketIcon, GithubIcon, GitLabIcon } from 'components/Icons';
import { getIcon } from 'components/Icons/Components';
import { getIconColor } from 'components/Icons/Components/getIconColor';
import { limitedString } from 'utils/string';

export const renderResources = ({ row }) => {
  const sensitiveRecords = row?.total_sensitive_records;
  const rowCount = row?.row_count;
  const displayedRecords =
    rowCount != null && sensitiveRecords != null
      ? sensitiveRecords * rowCount
      : sensitiveRecords;

  return <Box pr={10}>{formatNumber(displayedRecords, 1)}</Box>;
};

export const getDataSourceIcon = type => {
  switch (type) {
    case SupportedServices.GitHubBranch:
      return <GithubIcon />;
    case SupportedServices.GitLabBranch:
      return <GitLabIcon />;
    case SupportedServices.BitBucketBranch:
      return <BitbucketIcon />;
    default:
      return getIcon(type);
  }
};

export const renderLabel = ({ label, color, length = 14 }) => (
  <Tag
    styles={{
      label: { color: color },
      tag: {
        bg: colorOpacity(color, 0.1),
        fontSize: 'sm',
        px: 1.5,
        border: 'none',
      },
    }}
    label={
      <CustomTooltip label={label} textAlign="left">
        <EllipsisText>{label}</EllipsisText>
      </CustomTooltip>
    }
  />
);

export const renderPlusCountTagWithTooltip = (values, length) => {
  return (
    <CustomTooltip
      label={
        <Stack spacing={0}>
          {map(values.slice(length, values?.length), o => (
            <Box>{o?.label ?? o}</Box>
          ))}
        </Stack>
      }
    >
      <Tag
        label={'+' + (values?.length - length)}
        styles={{
          tag: { fontSize: '13px', bg: 'primary' },
          label: { color: 'white' },
        }}
      />
    </CustomTooltip>
  );
};

export const renderLabels = value => (
  <Stack spacing={1}>
    <HStack spacing={1}>
      {value[0] && renderLabel(value[0])}
      {value[1] && renderLabel(value[1])}
    </HStack>
    <HStack spacing={1}>
      {value[2] && renderLabel(value[2])}
      {value?.length > 3 && (
        <PlusCountTag
          count={value?.length - 3}
          tooltip={formatTooltip({
            header: 'Labels',
            content: (
              <Stack>
                {map(value.slice(3), (o, index) => (
                  <Box>
                    {index}. {o?.label ?? o}
                  </Box>
                ))}
              </Stack>
            ),
          })}
        />
      )}
    </HStack>
  </Stack>
);

export const getSensitiveLabelBorderColor = type => {
  switch (type?.toLowerCase()) {
    case 'pii':
      return 'primary';
    case 'phi':
      return 'skyBlue.300';
    case 'pci':
      return 'blue.300';
    default:
      return 'green.300';
  }
};

export const getSensitiveLabelColor = type => {
  switch (type) {
    case 'PII':
    case 'pii':
    case 'PHI':
    case 'phi':
    case 'PCI':
    case 'pci':
      return getIconColor(type);
    default:
      return customTheme.colors.green['300'];
  }
};

export const getDataTypeLabels = row => {
  const labels =
    row?.source_labels ??
    row?.labels ??
    row?.data_type_labels ??
    row?.data_labels ??
    [];

  if (labels instanceof Set) return Array.from(labels);
  return Array.isArray(labels) ? labels : labels ? [labels] : [];
};

// Prefer the PII color for a data type name, then fall back to its first label.
export const getDataTypeNameColor = row => {
  const labels = getDataTypeLabels(row);
  const label =
    labels.find(
      currentLabel =>
        typeof currentLabel === 'string' &&
        currentLabel.toUpperCase() === 'PII',
    ) ?? labels[0];

  return label
    ? getSensitiveLabelColor(label)
    : getIconColor(row?.sensitivity_level);
};

export const renderDataTypeLabels = ({ row }) =>
  renderLabels(
    map(getDataTypeLabels(row), o => ({
      label: o,
      color: getSensitiveLabelColor(o),
    })),
  );
